import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { SlotContent } from '@utils/Slot'
import React, { useState } from 'react'
import JackpotStatistics from '../common/JackpotStatistics'
import { defaultJackpotCardsConfig, jackpotCardsConfig } from './config'
import JackpotCardsStatisticsItem from './JackpotCardsStatisticsItem'
import JackpotMovingSuit from './JackpotMovingSuit'
import { GameType, JackpotSuits, ViewportType } from 'game-ui-types'

/**
 * JackpotCardsSection Component
 *
 * Handles rendering of all jackpot cards including jackpot card flying/moving small suit.
 */
const JackpotCardsSection: React.FC = () => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const gameType = useConfigurationStore((state) => state.gameType)
    const jackpotCardsSectionConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotCardsSection').position
    )
    const jackpotCardsLogoConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotCardsLogo').position
    )
    const jackpotCardsLogoConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotCardsLogo').dimensions
    )
    const jackpotCardsLogoConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('JackpotCardsLogo').styles
    )

    const hasLeadElement = useJackpotStore((state) => state.hasLeadElement)
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)

    const { images } = defaultJackpotCardsConfig[viewport]
    const [showTooltipSuit, setShowTooltipSuit] = useState<JackpotSuits | null>(null)

    const logoStyle = {
        ...jackpotCardsLogoConfigPosition,
        ...jackpotCardsLogoConfigDimensions,
    }

    const canShowLogo =
        (viewport === ViewportType.MOBILE_PORTRAIT &&
            !hasLeadElement &&
            (gameType === GameType.SLOT_CONFIG_A || gameType === GameType.SLOT_CONFIG_B)) ||
        isJackpotRoundRunning

    const handleClick = (suit: JackpotSuits) => {
        if (suit === showTooltipSuit) setShowTooltipSuit(null)
        else setShowTooltipSuit(suit)
    }

    const handleMouseOver =
        viewport !== ViewportType.DESKTOP
            ? undefined
            : (suit: JackpotSuits) => {
                  setShowTooltipSuit(suit)
              }

    const handleMouseOut =
        viewport !== ViewportType.DESKTOP
            ? undefined
            : () => {
                  setShowTooltipSuit(null)
              }

    return (
        <>
            <JackpotStatistics
                globalConfigPositions={jackpotCardsSectionConfigPosition}
                config={defaultJackpotCardsConfig}
            >
                <SlotContent slot="top">
                    <JackpotCardsStatisticsItem
                        type={JackpotSuits.SPADES}
                        onClick={handleClick}
                        onMouseOver={handleMouseOver}
                        onMouseOut={handleMouseOut}
                        isTooltipActive={showTooltipSuit === JackpotSuits.SPADES}
                    />
                    <JackpotCardsStatisticsItem
                        type={JackpotSuits.HEARTS}
                        onClick={handleClick}
                        onMouseOver={handleMouseOver}
                        onMouseOut={handleMouseOut}
                        isTooltipActive={showTooltipSuit === JackpotSuits.HEARTS}
                    />
                </SlotContent>
                {canShowLogo && (
                    <div slot="logo" className={jackpotCardsLogoConfigStyles} style={logoStyle}>
                        <img src={images.logo} alt="Jackpot Cards Logo" />
                    </div>
                )}
                <SlotContent slot="bottom">
                    <JackpotCardsStatisticsItem
                        type={JackpotSuits.DIAMONDS}
                        onClick={handleClick}
                        onMouseOver={handleMouseOver}
                        onMouseOut={handleMouseOut}
                        isTooltipActive={showTooltipSuit === JackpotSuits.DIAMONDS}
                    />
                    <JackpotCardsStatisticsItem
                        type={JackpotSuits.CLUBS}
                        onClick={handleClick}
                        onMouseOver={handleMouseOver}
                        onMouseOut={handleMouseOut}
                        isTooltipActive={showTooltipSuit === JackpotSuits.CLUBS}
                    />
                </SlotContent>
            </JackpotStatistics>
            {isJackpotRoundRunning && <JackpotMovingSuit allConfigs={jackpotCardsConfig} />}
            {viewport !== ViewportType.DESKTOP && showTooltipSuit && (
                <div className="overlay w-full h-full top-0 left-0 fixed" onClick={() => setShowTooltipSuit(null)} />
            )}
        </>
    )
}

export default JackpotCardsSection
